import { siteApi } from "@api";
import useServerInfo from "@hooks/useServerInfo";
import useUserMode from "@hooks/useUserMode";
import { getDiff } from "@utils/reward";
import { throttle } from "lodash-es";
import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

export default function DailyReferBallClaimOnRouting() {
  const location = useLocation();
  const [claimReferral] = siteApi.useClaimReferralDailyTokensMutation();
  const { isGuest } = useUserMode();
  const {
    data: referralRewardStatus,
    isLoading: isFetchingReferralRewardStatus,
    refetch: refetchReferralRewardStatus,
  } = siteApi.useGetReferralRewardStatusQuery(undefined, {
    skip: isGuest,
  });
  const serverInfo = useServerInfo();

  const throttledClaim = useMemo(
    () =>
      throttle(
        async () => {
          try {
            await claimReferral().unwrap();
            // Refetch the referral reward status after successful claim
            refetchReferralRewardStatus();
          } catch (error) {
            console.error("Failed to claim referral:", error);
          }
        },
        1000,
        { leading: true, trailing: false },
      ),
    [claimReferral, refetchReferralRewardStatus],
  );

  const isReferralRewardClaimable = useMemo(() => {
    if (!referralRewardStatus?.enabled) return false;
    if (isFetchingReferralRewardStatus || !referralRewardStatus?.canParticipate) return false;
    const lastClaimedAt = referralRewardStatus?.dailyFreeSetting?.lastDailyRefTokenClaimedAt;
    const currentPeriodResetDate = referralRewardStatus?.dailyFreeSetting?.currentPeriodResetDate ?? undefined;
    const nextPeriodResetDate = referralRewardStatus?.dailyFreeSetting?.nextPeriodResetDate ?? undefined;

    return (
      getDiff(
        {
          lastClaimedAt,
          currentPeriodResetDate,
          nextPeriodResetDate,
        },
        serverInfo?.utcOffset ?? 0,
      ) <= 0
    );
  }, [referralRewardStatus, serverInfo?.utcOffset, isFetchingReferralRewardStatus]);

  useEffect(() => {
    if (isReferralRewardClaimable) {
      throttledClaim();
    }
  }, [location, isReferralRewardClaimable, throttledClaim]);

  return null;
}
